import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { Search } from "lucide-react";
import RealtimeBridge from "./components/RealtimeBridge";
import OmniBar from "./components/loop/OmniBar";
import SidebarNav from "./components/SidebarNav";
import { toast } from "@/ui";
import { auth as authApi, redirectToLogin } from "./lib/api";
import { WorkspaceShell } from "@/ui";
import { WorkspaceMenubar } from "./components/WorkspaceMenubar";
import { useKeyboardViewport } from "./hooks/useKeyboardViewport";
import { useNativePush } from "./hooks/useNativePush";
import NotificationCenter from "./components/NotificationCenter";
import AgentRequestsBadge from "./components/AgentRequestsBadge";
import { useWorkspaceAppearance } from "./hooks/useWorkspaceAppearance";
import {
    isNativeAuthConfigured,
    isNativeIOS,
    getNativeUserInfo,
    NATIVE_AUTH_REQUIRED_EVENT,
    signInNative,
} from "./lib/native-auth";

export default function App() {
    useKeyboardViewport();
    const [authReady, setAuthReady] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [signingIn, setSigningIn] = useState(false);

    const [omniOpen, setOmniOpen] = useState(false);
    const navigate = useNavigate();
    useWorkspaceAppearance(authReady);

    const checkSession = useCallback(async () => {
        if (isNativeIOS()) {
            const user = await getNativeUserInfo();
            if (!user) throw new Error("Sign in is required.");
            setAuthError(null);
            setAuthReady(true);
            return;
        }
        const session = await authApi.session();
        if (!session?.user) throw new Error("Sign in is required.");
        setAuthError(null);
        setAuthReady(true);
    }, []);

    // Guard the shell: if there's no Better Auth session, bounce to login
    // immediately rather than waiting for the first data fetch to 401.
    useEffect(() => {
        void checkSession().catch(error => {
            if (isNativeIOS()) {
                setAuthError(isNativeAuthConfigured()
                    ? (error instanceof Error ? error.message : "Sign in is required.")
                    : "Native OAuth is not configured for this build. Add the iOS client ID and rebuild.");
            } else {
                redirectToLogin();
            }
        });
        const requireNativeAuth = () => {
            setAuthReady(false);
            setAuthError("Your session expired. Sign in again to continue.");
        };
        window.addEventListener(NATIVE_AUTH_REQUIRED_EVENT, requireNativeAuth);
        return () => window.removeEventListener(NATIVE_AUTH_REQUIRED_EVENT, requireNativeAuth);
    }, [checkSession]);

    const openPushRoute = useCallback((route: string) => navigate(route), [navigate]);
    const pushRegistered = useCallback(() => toast.success("Notifications enabled"), []);
    const pushError = useCallback((message: string) => toast.error(message), []);
    const push = useNativePush({
        enabled: authReady,
        onOpenRoute: openPushRoute,
        onRegistered: pushRegistered,
        onError: pushError,
    });

    const signIn = useCallback(async () => {
        setSigningIn(true);
        setAuthError(null);
        try {
            await signInNative();
            await checkSession();
        } catch (error) {
            setAuthError(error instanceof Error ? error.message : "Sign in failed.");
        } finally {
            setSigningIn(false);
        }
    }, [checkSession]);

    // Global ⌘K / Ctrl+K — the jump-or-capture overlay.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOmniOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    if (!authReady) {
        return (
            <main className="auth-gate" aria-live="polite">
                <div className="auth-gate-mark" aria-hidden="true">K</div>
                <p>{authError ?? "Connecting to Kardoe…"}</p>
                {isNativeIOS() && (
                    <button
                        className="primary"
                        type="button"
                        onClick={() => void signIn()}
                        disabled={signingIn || !isNativeAuthConfigured()}
                    >
                        {signingIn ? "Opening sign in…" : "Sign in"}
                    </button>
                )}
            </main>
        );
    }

    return (
        <WorkspaceShell
            sidebar={<SidebarNav />}
            mobileSidebar={close => <SidebarNav onNavigate={close} />}
            header={<>
                        <RealtimeBridge />
                        <div className="menubar-brand"><span>K</span><strong>Kardoe OS</strong></div>
                        <WorkspaceMenubar />
                        <button className="global-search global-search-trigger" type="button" onClick={() => setOmniOpen(true)} aria-label="Search Kardoe OS">
                            <Search aria-hidden="true" /><span>Search…</span><kbd>⌘ K</kbd>
                        </button>
                        <div className="header-actions">
                            <AgentRequestsBadge />
                            <NotificationCenter
                                native={push.isNative}
                                nativeState={push.state}
                                requestNative={push.requestPermission}
                            />
                        </div>
            </>}
        >
            <Outlet />
            <OmniBar open={omniOpen} onClose={() => setOmniOpen(false)} />
        </WorkspaceShell>
    );
}
